import { Injectable } from '@angular/core';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { PrincipalSource, PrincipalType, sp } from '@pnp/sp';
import { IItemAddResult } from '@pnp/sp/items';
import { IPeoplePickerEntity } from '@pnp/sp/profiles';
import { ISiteUserInfo } from '@pnp/sp/site-users/types';
import { GraphHttpClient } from '../auth';
import { UserAccessRequest, UserCertification } from './models';
import { execute, FatalError } from './utils';

export interface IUserProfile {
  AccountName?: string;
  DisplayName?: string;
  Email?: string;
  PictureUrl?: string;
  ProfileGuid?: string;
  WorkPhone?: string;
  Department?: string;
  Manager?: string;
  AboutMe?: string;
  UserName?: string;
  JobTitle?: string;
  UserPrincipalName?: string;
  CellPhone?: string;
  HomePhone?: string;
  MsOnlineObjectId?: string;
  MyAbbVieFunction?: string;
  Country?: string;
  UserTimeZone?: string;
  MyAbbVieArea?: string;
  MyAbbVieLocation?: string;
}

const profileProperties = {
  AccountName: ['AccountName'],
  DisplayName: ['DisplayName'],
  Email: ['Email'],
  PictureUrl: ['PictureUrl', 'PictureURL'],
  ProfileGuid: ['UserProfile_GUID'],
  WorkPhone: ['WorkPhone'],
  Department: ['Department', 'SPS-Department'],
  Manager: ['Manager'],
  AboutMe: ['AboutMe'],
  UserName: ['UserName'],
  JobTitle: ['SPS-JobTitle'],
  UserPrincipalName: ['SPS-UserPrincipalName'],
  CellPhone: ['CellPhone'],
  HomePhone: ['HomePhone'],
  Country: ['Country'],
  MsOnlineObjectId: ['msOnline-ObjectId'],
  UserTimeZone: ['UserTimeZone'],
  MyAbbVieFunction: ['MyAbbVieFunction'],
  MyAbbVieArea: ['MyAbbVieArea'],
  MyAbbVieLocation: ['MyAbbVieLocation']
}

@Injectable({
  providedIn: 'root'
})
export class UsersService {

  constructor(private graph: GraphHttpClient, private sanitizer: DomSanitizer) { }

  searchGroup(searchText: string): Promise<IPeoplePickerEntity[]> {	
    return execute(async () => sp.profiles.clientPeoplePickerSearchUser({	
      QueryString: searchText,	
      AllowEmailAddresses: true,	
      AllowOnlyEmailAddresses: false,	
      MaximumEntitySuggestions: 25,	
      PrincipalType: PrincipalType.User,	
      PrincipalSource: PrincipalSource.All,	
      //SharePointGroupID: await this.getSharePointGroupId('ContractRightsApprovers') //387	
      SharePointGroupID: await sp.web.siteGroups.getByName('ContractRightsApprovers').get().then(grp=>{return grp.Id;})      	
    }));	
  }

  async getContractRightApproverGroupList():Promise<number>{
    return await sp.web.siteGroups.getByName('ContractRightsApprovers').get().then(grp=>{return grp.Id;});
  }
  
  search(searchText: string): Promise<IPeoplePickerEntity[]> {
    return execute(() => sp.profiles.clientPeoplePickerSearchUser({
      QueryString: searchText,
      AllowEmailAddresses: true,
      AllowOnlyEmailAddresses: false,
      MaximumEntitySuggestions: 25,
      PrincipalType: PrincipalType.User,
      PrincipalSource: PrincipalSource.All
    }));
  }

  async getCurentUserProfile(): Promise<IUserProfile> {
    const properties = await sp.profiles.myProperties.get();
    const profile: any = {};
    for (const prop in properties) {
      const value = properties[prop];
      if (prop !== 'UserProfileProperties' && value !== '' && value != null) {
        profile[prop] = properties[prop];
      }
    }
    for (const userProfileProperty of properties.UserProfileProperties) {
      const value = profile[userProfileProperty.Key] ||
        userProfileProperty.Value;
      if (value !== '' && value != null) {
        profile[userProfileProperty.Key] = value;
      }
    }
    const result: any = {};
    for (const propName in profileProperties) {
      const sourceProperties: string[] = profileProperties[propName];
      for (const sourceProperty of sourceProperties) {
        const sourcePropertyValue = profile[sourceProperty];
        if (sourcePropertyValue != null) {
          result[propName] = sourcePropertyValue;
          break;
        }
      }
    }
    return result;
  }
  async getProfileByLoginName(loginName: string): Promise<IUserProfile> {
    const properties = await sp.profiles.getPropertiesFor(loginName);
    const profile: any = {};
    for (const prop in properties) {
      const value = properties[prop];
      if (prop !== 'UserProfileProperties' && value !== '' && value != null) {
        profile[prop] = properties[prop];
      }
    }
    for (const userProfileProperty of properties.UserProfileProperties) {
      const value = profile[userProfileProperty.Key] ||
        userProfileProperty.Value;
      if (value !== '' && value != null) {
        profile[userProfileProperty.Key] = value;
      }
    }
    const result: any = {};
    for (const propName in profileProperties) {
      const sourceProperties: string[] = profileProperties[propName];
      for (const sourceProperty of sourceProperties) {
        const sourcePropertyValue = profile[sourceProperty];
        if (sourcePropertyValue != null) {
          result[propName] = sourcePropertyValue;
          break;
        }
      }
    }
    return result;
  }
  async getUserPhoto(principalName: string): Promise<SafeUrl> {
    const options = { method: 'GET' };
    // https://graph.microsoft.com/v1.0/users/tst-abvdev01@devlabs365.onmicrosoft.com/photo/$value
    const url = `https://graph.microsoft.com/v1.0/users/${principalName}/photo/$value`;
    const imageBlob = await execute(async () => {
      const imageResponse = await this.graph.fetch(url, options);
      if (imageResponse.ok) {
        return imageResponse.blob();
      }
      else {
        //throw new FatalError('Error code: ' + imageResponse.status)
        return undefined;
      }
    })
    const safeUrl = imageBlob ?  this.sanitizer.bypassSecurityTrustUrl(URL.createObjectURL(imageBlob)) : "";
    return safeUrl;
  }

  async getCurrentUserPhoto(): Promise<SafeUrl> {
    const options = { method: 'GET' };
    const url = 'https://graph.microsoft.com/v1.0/me/photo/$value';
    const imageBlob = await execute(async () => {
      const imageResponse = await this.graph.fetch(url, options);
      if (imageResponse.ok) {
        return imageResponse.blob();
      }
      else {
        throw new Error('Error code: ' + imageResponse.status)
      }
    })
    const safeUrl = this.sanitizer.bypassSecurityTrustUrl(URL.createObjectURL(imageBlob));
    return safeUrl;
  }

  async getProfileByUserId(userId: number): Promise<IUserProfile> {
    const user = await sp.web.getUserById(userId).get();
    return this.getProfileByLoginName(user.LoginName);
  }

  async getProfileByUserEmail(email: string): Promise<IUserProfile> {
    const user = await sp.web.siteUsers.getByEmail(email).get();
    return this.getProfileByLoginName(user.LoginName);
  }

  async getUserById(userId: number): Promise<ISiteUserInfo> {
    return execute(() => sp.web.getUserById(userId).get());
  }

  async ensureUserId(loginName: string): Promise<number> {
    const ensureUserResult = await execute(() => sp.web.ensureUser(loginName));
    return ensureUserResult.data.Id;
  }

  ensureUserIds(loginNames: string[]): Promise<number[]> {
    if (!loginNames || loginNames.length === 0) return Promise.resolve([]);
    const promises = loginNames.map(loginName => this.ensureUserId(loginName));
    return Promise.all(promises)
  }

  ensureUser(loginName: string): Promise<ISiteUserInfo> {
    return execute(async () => {
      try {
        const result = await sp.web.ensureUser(loginName);
        return await sp.web.siteUsers.getById(result.data.Id).get();
      }
      catch (err) {
        try {
          return await sp.web.siteUsers.getByLoginName(loginName).get();
        }
        catch (err1) {
          return await sp.web.siteUsers.getByEmail(loginName).get();
        }
      }
    });
  }
  ensureUserByEmail(email: string): Promise<ISiteUserInfo> {
    return execute(async () => {
      try {
        return await sp.web.siteUsers.getByEmail(email).get();
      }
      catch (err) {
        console.log("Error get user by id " + err);
        return null;
      }
    });
  }

  ensureUserById(id: number): Promise<ISiteUserInfo> {
    return execute(async () => {
      try {
        return await sp.web.siteUsers.getById(id).get();
      }
      catch (err) {
        console.log("Error get user by id " + err);
        return null;
      }
    });
  }

  getAllSiteUsers(): Promise<ISiteUserInfo[]> {
    return execute(() => sp.web.siteUsers.get());
  }

  getMembersOfGroup(groupId: number): Promise<ISiteUserInfo[]> {
    return execute(() => sp.web.siteGroups.getById(groupId).users.get());
  }

  addUserToGroup(user: ISiteUserInfo, groupId: number): Promise<void> {
    return execute(async () => {
      if (await this.isUserMemberOfGroup(user.Id, groupId)) {
        return;
      }
      await sp.web.siteGroups.getById(groupId).users.add(user.LoginName);
    });
  }

  private async isUserMemberOfGroup(userId: number, groupId: number): Promise<boolean> {
    try {
      const group = await sp.web.siteUsers.getById(userId).groups.getById(groupId).get();
      return !!group;
    }
    catch {
      return false;
    }
  }

  public async getUserCertification(userId: number): Promise<UserCertification> {
    const result = await sp.web.lists
      .getByTitle("UserCertification")
      .items.filter(`AuthorId eq ${userId}`).top(1).orderBy("Modified", false).get();

    if (result.length === 0)
      return undefined;
    else
      return {
        ID: result[0].ID,
        Title: result[0].Title,
        CertificationDate: new Date(result[0].CertificationDate),
        ExpirationDate: new Date(result[0].ExpirationDate)
      } as UserCertification;
  }

  public async getUserAccessRequest(userId: number): Promise<UserAccessRequest[]> {
    const result = await sp.web.lists
      .getByTitle("UserAccessRequests")
      .expand('Author')
      .items.filter(`AuthorId eq ${userId} and Status eq 'Submitted' `).get();

    if (result.length === 0)
      return undefined;
    else
      return result;
  }

  public async storeAccessRequest(user: string, userInput: string): Promise<number> {
    const listItem = {
      Title: user,
      RequestDetails: userInput,
    };
    return (await sp.web.lists.getByTitle("UserAccessRequests").items.add(listItem)).data.Id;
  }

  public async createUserCertification(certification: UserCertification): Promise<IItemAddResult> {
    return sp.web.lists.getByTitle("UserCertification").items.add(certification);
  }

  public async updateUserCertification(certification: UserCertification): Promise<IItemAddResult> {
    return sp.web.lists.getByTitle("UserCertification").items.getById(certification.Id).update(certification);
  }

  async getCountryPhoto(id): Promise<any> {
    const countryImg = await sp.web.lists.getByTitle("Countries").items.getById(id)();
    return countryImg;

  }

  async getGroupListID(groupName):Promise<number>{
    return await sp.web.siteGroups.getByName(groupName).get().then(grp=>{return grp.Id;});
  }

}
